import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, Modal, Typography, Button, useMediaQuery } from "@mui/material";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import { ArrowLeft, Camera } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import { api } from "../services/api";
import { ToastContainer, toast } from "react-toastify";
import { useAuth } from "../hooks/authBackoffice";
import { useThemeStore } from "../store/useThemeStore";
import { v4 as uuidv4 } from "uuid";
import { createPortal } from "react-dom";

const CameraPermissionComponent: React.FC = () => {
  const [scannerOpen, setScannerOpen] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [error, setError] = useState("");
  const [cameraPermission, setCameraPermission] = useState<"granted" | "denied" | "prompt" | "unsupported">("prompt");
  const [isLoadingCamera, setIsLoadingCamera] = useState(true);
  const [isIosDevice, setIsIosDevice] = useState(false);
  const html5QrCodeRef = useRef<Html5Qrcode | null>(null);
  const token = localStorage.getItem("@backoffice:token");

  const { user } = useAuth();

  const { theme } = useThemeStore();

  // Função que solicita a permissão e abre a câmera
  const handleCameraAccess = async () => {
    try {
      checkCameraPermission();
      setScannerOpen(true);
    } catch (err) {
      console.log("deu erro no acesso", err);
      // Se a permissão for negada ou ocorrer algum erro
      console.error("Erro ao acessar a câmera: ", err);
      setScannerOpen(false);
      setModalOpen(true); // Abrir o modal de permissão
    }
  };
  // Check if iOS/Safari
  const checkIsIos = () => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIosDevice = /iphone|ipad|ipod/.test(userAgent);
    const isSafari = /safari/.test(userAgent) && !/chrome|crios|fxios|edg|opr/.test(userAgent);
    setIsIosDevice(isIosDevice || isSafari);
  };

  const isPermissionsAPISupported = "permissions" in navigator;

  const handleQrCodeResult = async (decodedText: string) => {
    console.log("QR Code lido:", decodedText);

    try {
      await api.post(
        `qr-login`,
        { token: decodedText, user },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast("Login realizado com sucesso!", { type: "success" });
    } catch (error: any) {
      toast(error.response.data.error, { type: "error" });
      console.error(error.response.data.error);
    }

    stopScanner();
    setScannerOpen(false);
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        await html5QrCodeRef.current.clear();
        html5QrCodeRef.current = null;
      } catch (err) {
        console.error("Error stopping scanner:", err);
      }
    }
  };

  const startQRScanner = () => {
    const config = {
      fps: 20,
      aspectRatio: isIosDevice ? 1.777 : 1.777,
      facingMode: { exact: "environment" },
    };

    const html5QrCode = new Html5Qrcode("reader");
    html5QrCodeRef.current = html5QrCode;

    html5QrCode
      .start(
        { facingMode: "environment" },
        config,
        (decodedText) => {
          console.log("deu erro no start", decodedText);
          html5QrCode?.stop();
          handleQrCodeResult(decodedText);
        },
        (error) => {
          console.error("Erro ao ler a linha digitável:", error);
        }
      )
      .catch((err) => {
        console.log("deu erro no start", err);
        console.error("Camera access error:", err);
        setError("Erro ao acessar a câmera. Por favor, tente novamente.");
      });
  };

  // Camera permission handling
  const checkCameraPermission = async () => {
    setIsLoadingCamera(true);
    try {
      console.log("é?", isPermissionsAPISupported);
      if (isPermissionsAPISupported) {
        const permissionStatus = await navigator.permissions.query({
          name: "camera" as PermissionName,
        });
        console.log("permissionStatus", permissionStatus);
        setCameraPermission(permissionStatus.state);

        permissionStatus.onchange = () => {
          setCameraPermission(permissionStatus.state);
          console.log("entrou a?, antes");

          if (permissionStatus.state === "granted") {
            console.log("entrou a?, garantido");
            startQRScanner();
          } else if (permissionStatus.state === "denied") {
            console.log("entrou a?, negada");

            setError(
              "Permissão da câmera negada. Por favor, habilite o acesso à câmera nas configurações do navegador."
            );
          }
        };

        if (permissionStatus.state === "granted") {
          console.log("startou?");
          setScannerOpen(true);
        } else if (permissionStatus.state === "prompt") {
          console.log("se n estartou, ta aq");
          const stream = await navigator.mediaDevices.getUserMedia({
            video: true,
          });
          console.log("streamn", stream);
          stream.getTracks().forEach((track) => track.stop());
          setScannerOpen(true);
        } else {
          setError("Permissão da câmera negada. Por favor, habilite o acesso à câmera nas configurações do navegador.");
        }
      } else {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: true,
          });
          stream.getTracks().forEach((track) => track.stop());
          setScannerOpen(true);
        } catch (error) {
          setError(
            "Não foi possível acessar a câmera. Por favor, habilite o acesso à câmera nas configurações do navegador."
          );
        }
      }
    } catch (error) {
      console.log("deu erro no check", error);
      console.error("Camera permission error:", error);
      setError("Erro ao acessar a câmera. Por favor, tente novamente.");
    } finally {
      setIsLoadingCamera(false);
    }
  };

  const openSettings = () => {
    if (/iPhone|iPad|iPod/.test(navigator.userAgent)) {
      alert("Vá para Configurações > Safari > Câmera e habilite o acesso.");
    } else if (/Android/.test(navigator.userAgent)) {
      alert("Vá para Configurações > Apps > [Nome do Navegador] > Permissões e habilite o acesso à câmera.");
    } else {
      alert("Por favor, habilite a permissão da câmera nas configurações do seu navegador.");
    }
  };

  const handleCloseCamera = async () => {
    await stopScanner();
    setScannerOpen(false);
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (scannerOpen && document.getElementById("reader")) {
        startQRScanner();
      }
    }, 300); // tempo para garantir que o DOM atualizou

    return () => clearTimeout(timeout);
  }, [scannerOpen]);

  useEffect(() => {
    checkIsIos();
    return () => {
      stopScanner();
    };
  }, []);

  return (
    <Box sx={{ width: "auto", height: "auto", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <ToastContainer />

      <IconButton
        onClick={handleCameraAccess}
        sx={{
          width: 34,
          height: 34,
          color: theme === "dark" ? "#afbac0" : "#2a2a2a",
          backgroundColor: "transparent",
          "&:hover": { backgroundColor: "transparent", opacity: 0.75 },
        }}
      >
        <PhotoCameraOutlinedIcon sx={{ fontSize: 21 }} />
      </IconButton>

      {/* Modal que aparece quando o usuário não concedeu permissão */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "white",
            padding: 3,
            borderRadius: 2,
            boxShadow: 24,
            width: isMobile ? "80%" : 400,
            textAlign: "center",
          }}
        >
          <Typography variant="h6" id="modal-modal-title">
            Permitir acesso à câmera?
          </Typography>
          <Typography variant="body2" id="modal-modal-description" sx={{ marginTop: 1 }}>
            Para que possamos escanear códigos, por favor, permita o acesso à sua câmera durante o uso do app.
          </Typography>
          <Button variant="contained" sx={{ marginTop: 2 }} onClick={() => setModalOpen(false)}>
            Entendido
          </Button>
        </Box>
      </Modal>

      {/* Se a câmera estiver aberta, exibe o vídeo na tela cheia */}
      {scannerOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "102%",
            height: isIosDevice ? "120vh" : "120vh",
            zIndex: 1300,
          }}
        >
          {isLoadingCamera && (
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                color: "#fff",
                fontSize: "18px",
                textAlign: "center",
              }}
            >
              <p>Inicializando câmera...</p>
              <div style={{ marginTop: "10px", fontSize: "24px" }}>🌀</div>
            </div>
          )}

          {/* Seta de Voltar */}
          <button
            onClick={async () => {
              await stopScanner();
              setScannerOpen(false);
            }}
            type="button"
            style={{
              position: "absolute",
              top: "40px",
              left: "20px",
              background: "transparent",
              border: "none",
              color: "white",
              fontSize: "24px",
              zIndex: 1600,
            }}
          >
            <ArrowLeft />
          </button>

          <div
            style={{
              position: "absolute",
              top: "100px",
              left: "50%",
              transform: "translateX(-50%)",
              textAlign: "center",
              color: "white",
              zIndex: 1600,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <Camera />
            <p style={{ fontSize: "16px", fontWeight: "500" }}>Aponte a câmera para o QR code para realizar o login</p>
          </div>

          <div
            id="reader"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              minHeight: "150%",
              minWidth: "150%",
              zIndex: 1300,
            }}
          ></div>

          <div
            style={{
              position: "absolute",
              top: "40%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "250px",
              height: "250px",
              border: "2px solid white",
              borderRadius: "12px",
              boxShadow: "0 0 0 100vmax rgba(0, 0, 0, 0.5)",
              zIndex: 1400,
            }}
          ></div>

          {error &&
            createPortal(
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100vw", // Use vw/vh para ocupar a tela inteira
                  height: "100vh",
                  backgroundColor: "rgba(0, 0, 0, 0.7)", // Um pouco mais escuro
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 9999, // Um z-index bem alto para garantir que fique na frente de tudo
                }}
              >
                <div
                  style={{
                    backgroundColor: "white",
                    color: "black", // Garante que o texto seja visível
                    borderRadius: "12px",
                    padding: "24px",
                    textAlign: "center",
                    width: "90%",
                    maxWidth: "320px",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.2)", // Adiciona uma sombra
                  }}
                >
                  <p
                    style={{
                      fontSize: "18px",
                      fontWeight: "500",
                      marginBottom: "16px",
                      lineHeight: "1.4", // Melhora a legibilidade
                    }}
                  >
                    {error}
                  </p>
                  <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
                    <button
                      onClick={handleCloseCamera}
                      style={{
                        background: "transparent",
                        border: "1px solid #ddd", // Adiciona uma borda sutil
                        color: "#004A8A",
                        fontSize: "16px",
                        fontWeight: "600",
                        padding: "8px 16px",
                        borderRadius: "8px",
                        cursor: "pointer", // Adiciona cursor de ponteiro
                      }}
                    >
                      Voltar
                    </button>
                    {cameraPermission === "denied" && (
                      <button
                        onClick={openSettings}
                        style={{
                          background: "#004A8A",
                          border: "none",
                          color: "white",
                          fontSize: "16px",
                          fontWeight: "600",
                          padding: "8px 16px",
                          borderRadius: "8px",
                          cursor: "pointer",
                        }}
                      >
                        Habilitar
                      </button>
                    )}
                  </div>
                </div>
              </div>,
              document.body // O segundo argumento do portal: onde renderizar
            )}
        </div>
      )}
    </Box>
  );
};

export default CameraPermissionComponent;
